import { SearchIcon, CloseIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  VStack,
  Avatar,
  useToast,
} from '@chakra-ui/react'
import { useMemo, useState } from 'react'
import type { Location, PrescriberProfile } from '../../../types/location'
import { formatPrescriberName } from '../../../types/location'
import { getAvatarColorById } from '../constants/avatarColors'
import { useRemovePrescriberMutation } from '../locationSlice'

interface ManagePrescribersPanelProps {
  location: Location
  prescribers: PrescriberProfile[]
}

export const ManagePrescribersPanel = ({ location, prescribers }: ManagePrescribersPanelProps) => {
  const [searchValue, setSearchValue] = useState('')
  const [prescriberToRemove, setPrescriberToRemove] = useState<string | null>(null)
  const [removePrescriber, { isLoading: isRemoving }] = useRemovePrescriberMutation()
  const toast = useToast()

  const filteredPrescribers = useMemo(() => {
    const normalizedQuery = searchValue.trim().toLowerCase()
    if (!normalizedQuery) return prescribers

    return prescribers.filter((prescriber) => {
      const name = formatPrescriberName(prescriber).toLowerCase()
      return name.includes(normalizedQuery)
    })
  }, [prescribers, searchValue])

  const handleRemoveClick = (prescriberId: string) => {
    setPrescriberToRemove(prescriberId)
  }

  const handleCancelRemove = () => {
    setPrescriberToRemove(null)
  }

  const handleConfirmRemove = async (prescriberId: string) => {
    const result = await removePrescriber({ locationId: location.id, prescriberId })

    if ('error' in result) {
      const error = result.error as { status?: number; data?: { message?: string } }
      toast({
        title: 'Failed to remove prescriber',
        description: error.data?.message ?? 'Something went wrong. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      })
    } else {
      toast({
        title: 'Prescriber removed',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      })
    }

    setPrescriberToRemove(null)
  }

  return (
    <VStack spacing={4} align="stretch" w="full">
      <InputGroup size="md">
        <InputLeftElement pointerEvents="none" color="gray.400">
          <SearchIcon boxSize={4} />
        </InputLeftElement>
        <Input
          value={searchValue}
          onChange={(event) => setSearchValue(event.target.value)}
          placeholder="Search prescribers..."
          borderColor="gray.200"
          bg="gray.50"
          borderRadius="md"
          _hover={{ borderColor: 'gray.300' }}
          _focusVisible={{
            borderColor: 'blue.400',
            boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)',
          }}
          aria-label="Search prescribers"
        />
      </InputGroup>

      <VStack align="stretch" spacing={2}>
        {filteredPrescribers.length === 0 ? (
          <Box py={4}>
            <Text fontSize="sm" color="gray.500" textAlign="center">
              No prescribers found
            </Text>
          </Box>
        ) : (
          filteredPrescribers.map((prescriber) => {
            const prescriberName = formatPrescriberName(prescriber)
            const avatarName = [prescriber.givenNames, prescriber.familyName]
              .filter(Boolean)
              .join(' ')
            const avatarBg = getAvatarColorById(prescriber.prescriberId)

            return (
              <Flex
                key={prescriber.prescriberId}
                justify="space-between"
                align="center"
                py={2}
                px={1}
                bg="transparent"
              >
                <HStack spacing={4}>
                  <Avatar
                    size="sm"
                    name={avatarName}
                    bg={avatarBg}
                    color="white"
                    fontWeight="bold"
                    fontSize="xs"
                  />
                  <VStack align="flex-start" spacing={0}>
                    <Text fontSize="sm" fontWeight="bold" color="gray.900">
                      {prescriberName}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {prescriber.providerNumber
                        ? `Provider number: ${prescriber.providerNumber}`
                        : ''}
                    </Text>
                  </VStack>
                </HStack>

                {prescriberToRemove === prescriber.prescriberId ? (
                  <HStack spacing={3}>
                    <Text fontSize="sm" color="red.600" fontWeight="bold">
                      Remove?
                    </Text>
                    <Button
                      size="sm"
                      bg="red.600"
                      color="white"
                      _hover={{ bg: 'red.700' }}
                      onClick={() => handleConfirmRemove(prescriber.prescriberId)}
                      isLoading={isRemoving}
                      minW="60px"
                    >
                      Yes
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      borderColor="gray.200"
                      color="gray.700"
                      bg="gray.50"
                      _hover={{ bg: 'gray.100' }}
                      onClick={handleCancelRemove}
                      isDisabled={isRemoving}
                      minW="60px"
                    >
                      No
                    </Button>
                  </HStack>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    borderColor="red.200"
                    color="red.500"
                    bg="white"
                    _hover={{ bg: 'red.50', borderColor: 'red.300' }}
                    onClick={() => handleRemoveClick(prescriber.prescriberId)}
                    aria-label={`Remove ${prescriberName}`}
                    px={0}
                    minW="32px"
                  >
                    <Icon as={CloseIcon} boxSize={3} />
                  </Button>
                )}
              </Flex>
            )
          })
        )}
      </VStack>
    </VStack>
  )
}
